import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IPagination,
  IPriceItem,
  IUpdateB2BPrice,
} from "../../models/productData.model";
import {
  fetchAllProductsAndRawMaterials,
  fetchBOMPriceList,
  updateB2BPriceApi,
} from "../../api/priceList.api";
import { IBOM } from "../../models/bom.model";

interface PriceListState {
  products: IPriceItem[];
  rawMaterials: IPriceItem[];
  boms: IBOM[];
  selectedBOM: IBOM | null;
  productPagination: IPagination | null;
  rawMaterialPagination: IPagination | null;
  loading: boolean;
  error: string | null;
}

const initialState: PriceListState = {
  products: [],
  rawMaterials: [],
  boms: [],
  selectedBOM: null,
  productPagination: null,
  rawMaterialPagination: null,
  loading: false,
  error: null,
};

export const getAllProductsAndRawMaterials = createAsyncThunk(
  "priceList/fetch",
  async (params: any, { rejectWithValue }) => {
    try {
      return await fetchAllProductsAndRawMaterials(params);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch price list");
    }
  },
);

export const updateB2BPrice = createAsyncThunk(
  "priceList/updateB2B",
  async (payload: IUpdateB2BPrice, { rejectWithValue }) => {
    try {
      return await updateB2BPriceApi(payload);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  },
);

export const getBOMPriceList = createAsyncThunk(
  "priceList/getBOMPriceList",
  async (params: any, { rejectWithValue }) => {
    try {
      return await fetchBOMPriceList(params);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch BOMs");
    }
  },
);

const priceListSlice = createSlice({
  name: "priceList",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllProductsAndRawMaterials.pending, (state) => {
        state.loading = true;
      })

      .addCase(getAllProductsAndRawMaterials.fulfilled, (state, action) => {
        state.loading = false;

        state.products = action.payload.products.data || [];
        state.rawMaterials = action.payload.rawMaterials.data || [];

        state.productPagination = action.payload.products.pagination;
        state.rawMaterialPagination = action.payload.rawMaterials.pagination;
      })

      .addCase(getAllProductsAndRawMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateB2BPrice.pending, (state) => {
        state.loading = true;
      })

      .addCase(updateB2BPrice.fulfilled, (state, action) => {
        state.loading = false;
        const { type, item } = action.payload;

        if (type === "product") {
          state.products = state.products.map((p) =>
            p._id === item._id
              ? { ...p, b2bPrice: item.b2bPrice, b2bMargin: item.b2bMargin }
              : p,
          );
        } else {
          state.rawMaterials = state.rawMaterials.map((r) =>
            r._id === item._id
              ? { ...r, b2bPrice: item.b2bPrice, b2bMargin: item.b2bMargin }
              : r,
          );
        }
      })
      .addCase(updateB2BPrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getBOMPriceList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBOMPriceList.fulfilled, (state, action) => {
        state.loading = false;
        state.boms = action.payload || [];
      })
      .addCase(getBOMPriceList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default priceListSlice.reducer;
